import React, { useEffect, useState } from 'react';
import ArrowDown from '@uber/base-icons/arrow-down';
import ArrowUp from '@uber/base-icons/arrow-up';
import ChevronLeft from '@uber/base-icons/chevron-left';
import ChevronRight from '@uber/base-icons/chevron-right';
import { styled, useStyletron } from 'baseui';
import { type ButtonProps, Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { LabelSmall } from 'baseui/typography';
import useShortcut from '../../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../../image/labelling/providers/utils/shortcut';
import { NavigationContainer, SectionHeader, StyledHeading } from '../styled-component-overrides';
import type { ReviewAnnotationT } from '../types';
import { AttributeContainer } from './attribute-container';
import { InstanceList } from './instance-list';
type Props = {
  annotation: ReviewAnnotationT;
  onInstanceDelete?: (instanceIndex: number) => void;
};
const SubSectionHeader = styled(SectionHeader, ({
  $theme
}) => ({
  marginBottom: $theme.sizing.scale100
}));
const AttributeHeading = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale100,
  alignItems: 'center'
}));
const StyledDivider = styled('div', ({
  $theme
}) => ({
  padding: `0 ${$theme.sizing.scale100}`,
  color: $theme.colors.contentTertiary
}));
const navigationButtonProps: ButtonProps = {
  size: SIZE.mini,
  shape: SHAPE.circle,
  kind: KIND.tertiary
};
export const ObjectDetails = ({
  annotation,
  onInstanceDelete
}: Props) => {
  const [, theme] = useStyletron();
  const [currentInstanceIndex, setCurrentInstanceIndex] = useState<number>(0);
  const [isAttributeExpanded, setIsAttributeExpanded] = useState<boolean>(false);
  const {
    registerHotKeys
  } = useShortcut();
  useEffect(() => {
    const {
      removeHotKeys,
      errors
    } = registerHotKeys([{
      keys: ['k'],
      onAction: () => {
        if (currentInstanceIndex < (annotation?.instances?.length ?? 0) - 1) {
          setCurrentInstanceIndex(currentInstanceIndex + 1);
        }
      }
    }, {
      keys: ['j'],
      onAction: () => {
        if (currentInstanceIndex > 0) {
          setCurrentInstanceIndex(currentInstanceIndex - 1);
        }
      }
    }, {
      keys: ['t'],
      onAction: () => setIsAttributeExpanded(!isAttributeExpanded)
    }, {
      keys: ['d'],
      onAction: () => onInstanceDelete?.(currentInstanceIndex)
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [registerHotKeys, currentInstanceIndex, setCurrentInstanceIndex, isAttributeExpanded, setIsAttributeExpanded, onInstanceDelete, annotation.instances?.length]);
  return <>
      {annotation.instances && annotation.instances.length !== 0 && <>
          <SubSectionHeader>
            <StyledHeading $subSectionHeader>Instances</StyledHeading>
            <NavigationContainer>
              <Button {...navigationButtonProps} disabled={currentInstanceIndex === 0} onClick={() => setCurrentInstanceIndex(currentInstanceIndex - 1)} data-testid="prev-instance">
                <ChevronLeft />
              </Button>
              <Button {...navigationButtonProps} disabled={currentInstanceIndex === annotation.instances.length - 1} onClick={() => setCurrentInstanceIndex(currentInstanceIndex + 1)} data-testid="next-instance">
                <ChevronRight />
              </Button>
            </NavigationContainer>
          </SubSectionHeader>

          <InstanceList instances={annotation.instances} currentInstanceIndex={currentInstanceIndex} onInstanceSelect={setCurrentInstanceIndex} onInstanceDelete={onInstanceDelete} />
        </>}

      {annotation.attributes && <>
          <SubSectionHeader>
            <AttributeHeading>
              <StyledHeading $subSectionHeader>Attributes</StyledHeading>
              {annotation.instances && annotation.instances.length > 0 && <>
                  <StyledDivider>&#8226;</StyledDivider>
                  <LabelSmall color={theme.colors.contentTertiary}>
                    {/* @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'. */}
                    {`${annotation.instances[currentInstanceIndex].startIndex} -> ${annotation.instances[currentInstanceIndex].endIndex}`}
                  </LabelSmall>
                </>}
            </AttributeHeading>
            <Button size={SIZE.mini} shape={SHAPE.square} kind={KIND.secondary} onClick={() => setIsAttributeExpanded(!isAttributeExpanded)} data-testid="toggle-attribute-expansion">
              {isAttributeExpanded ? <ArrowUp /> : <ArrowDown />}
            </Button>
          </SubSectionHeader>

          <AttributeContainer
      // @ts-expect-error todo(ts-migration) TS2322 Type 'ReviewAnnotationAttributesT | undefined' is not assignable to type 'ReviewAnnotationAttributesT'.
      attributes={annotation.attributes[currentInstanceIndex]} isAttributeExpanded={isAttributeExpanded} />
        </>}
    </>;
};